(function () {
  // Small delay stops a flicker when the cursor sweeps across a grid of cards.
  var HOVER_DELAY = 60;

  var SUPPORTS_HOVER = !window.matchMedia
    || window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var REDUCED_MOTION = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var FADE_DURATION = REDUCED_MOTION ? 0 : 400;

  // When the cursor leaves a card the shopper has moved off the featured
  // image, the card returns to it, so the next hover always starts the same
  // way: featured -> second image. The return is a cross-fade, not a slide,
  // so the track never animates back across the images they just viewed.
  // Set false to leave cards parked on whatever image they were left on.
  var RETURN_TO_FEATURED_ON_LEAVE = true;

  function loadSlideImage(slide) {
    if (!slide) return;

    var img = slide.querySelector('img[data-carousel-src]');
    if (!img) return;

    var src = img.getAttribute('data-carousel-src');
    if (!src) return;

    img.setAttribute('data-src', src);
    img.removeAttribute('data-carousel-src');
    img.classList.add('Image--lazyLoad');

    if (window.lazySizes && window.lazySizes.loader) {
      window.lazySizes.loader.unveil(img);
    }
  }

  function ProductCardCarousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-carousel-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-carousel-slide]'));
    this.prevBtn = root.querySelector('[data-carousel-prev]');
    this.nextBtn = root.querySelector('[data-carousel-next]');
    this.index = 0;
    this.count = this.slides.length;

    // The slide the card rests on when nothing is happening. Normally the
    // featured image, but variant swatches can move it (see goToMedia).
    this.baseIndex = 0;
    // Set once the shopper uses the arrows, so leaving the card doesn't yank
    // them back to the featured image mid-browse.
    this.userNavigated = false;

    this.hoverSlide = null;
    this.hoverTimer = null;
    this.hoverActive = false;
    this.settleTimer = null;
    this.fadeOutTimer = null;
    this.fadingSlide = null;
    this.returnSlide = null;
    this.returnTimer = null;

    // Arrow presses waiting on an un-painted jump (see queueStep).
    this.pendingDelta = 0;
    this.pendingStep = false;

    // Markup cloned out of a card mid-fade carries the overlay's inline
    // styles with it — a slide stuck at opacity 0 and offset by a full width.
    // Wipe every slide clean before measuring anything.
    this.slides.forEach(function (slide) {
      this.clearHoverStyles(slide);
    }, this);

    if (!this.track || this.count < 2) {
      root.setAttribute('data-carousel-ready', 'true');
      return;
    }

    this.bind();
    this.goTo(0, false);
    root.setAttribute('data-carousel-ready', 'true');
  }

  ProductCardCarousel.prototype.bind = function () {
    this.root._productCardCarouselGoTo = this.goTo.bind(this);
    this.root._productCardCarouselGoToMedia = this.goToMedia.bind(this);

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        this.onArrow(-1);
      }.bind(this));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        this.onArrow(1);
      }.bind(this));
    }

    if (!SUPPORTS_HOVER) return;

    // mouseenter/mouseleave don't fire for descendants, so moving onto an
    // arrow or the title inside the card won't retrigger these.
    this.root.addEventListener('mouseenter', this.onPointerEnter.bind(this));
    this.root.addEventListener('mouseleave', this.onPointerLeave.bind(this));

    // Keyboard parity: tabbing into the card shows the same second image.
    this.root.addEventListener('focusin', this.onPointerEnter.bind(this));
    this.root.addEventListener('focusout', function (event) {
      if (event.relatedTarget && this.root.contains(event.relatedTarget)) return;
      this.onPointerLeave();
    }.bind(this));
  };

  /**
   * Normally there's nothing to tidy up here: the hover fade has already
   * settled into a real slide change, so this is a plain one-step slide.
   * Only a press that lands mid-fade has to commit something first, and that
   * commit jumps the track — hence the queue.
   */
  ProductCardCarousel.prototype.onArrow = function (delta) {
    var jumped = this.finishReturn();
    if (this.commitHover()) jumped = true;

    this.userNavigated = true;
    this.queueStep(delta, jumped);
  };

  /**
   * Slide one step. A transition that starts in the same frame as an instant
   * jump animates from the last *painted* position instead of the jump — the
   * previous image flashes and the track travels several slides. Waiting two
   * animation frames guarantees a paint in between. Presses arriving while a
   * step is queued fold into it rather than racing it.
   */
  ProductCardCarousel.prototype.queueStep = function (delta, deferred) {
    if (!deferred && !this.pendingStep) {
      this.goTo(this.index + delta, true);
      return;
    }

    this.pendingDelta += delta;
    if (this.pendingStep) return;
    this.pendingStep = true;

    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var total = self.pendingDelta;
        self.pendingDelta = 0;
        self.pendingStep = false;
        self.goTo(self.index + total, true);
      });
    });
  };

  /* ---------------------------------------------------------------------
   * Hover cross-fade
   *
   * The hover image is the next slide in the track, pulled back on top of
   * the resting slide with a transform and faded in with opacity. Nothing
   * is cloned and the track doesn't move during the fade.
   * ------------------------------------------------------------------ */

  ProductCardCarousel.prototype.hoverIndex = function () {
    return (this.baseIndex + 1) % this.count;
  };

  ProductCardCarousel.prototype.clearTimers = function () {
    if (this.hoverTimer) {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
    if (this.settleTimer) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
    if (this.fadeOutTimer) {
      window.clearTimeout(this.fadeOutTimer);
      this.fadeOutTimer = null;
    }
  };

  /**
   * Strip the overlay styles from a slide that's still fading out. A cancelled
   * fade would otherwise leave a slide sitting at opacity 0 with a transform
   * on it — invisible and offset, so arrowing onto it looks like a skipped
   * slide. Safe to call mid-fade: the slide snaps back to its own offscreen
   * slot, where there's nothing to see.
   */
  ProductCardCarousel.prototype.flushFadingSlide = function () {
    if (this.fadeOutTimer) {
      window.clearTimeout(this.fadeOutTimer);
      this.fadeOutTimer = null;
    }
    if (this.fadingSlide) {
      this.clearHoverStyles(this.fadingSlide);
      this.fadingSlide = null;
    }
  };

  ProductCardCarousel.prototype.clearHoverStyles = function (slide) {
    if (!slide) return;

    // Kill the transition before removing the transform, so nothing in the
    // theme's own CSS can animate the slide back towards its slot.
    slide.style.transition = 'none';
    slide.style.opacity = '';
    slide.style.transform = '';
    slide.style.zIndex = '';
    slide.style.pointerEvents = '';

    slide.offsetHeight; // eslint-disable-line no-unused-expressions
    slide.style.transition = '';

    if (slide._pcPositionForced) {
      slide.style.position = '';
      slide._pcPositionForced = false;
    }
  };

  /**
   * Park a slide on top of the one currently in view and fade it up. Used
   * both for the hover preview and for the return to the featured image.
   */
  ProductCardCarousel.prototype.fadeSlideIn = function (slide, zIndex) {
    // z-index is ignored on statically positioned elements.
    if (window.getComputedStyle(slide).position === 'static') {
      slide.style.position = 'relative';
      slide._pcPositionForced = true;
    }

    var offset = (this.index - this.slides.indexOf(slide)) * 100;

    slide.style.transform = 'translate3d(' + offset + '%, 0, 0)';
    slide.style.zIndex = String(zIndex);
    slide.style.pointerEvents = 'none'; // keep the underlying product link clickable
    slide.style.transition = 'none';
    slide.style.opacity = '0';

    slide.offsetHeight; // flush the starting state before transitioning

    slide.style.transition = 'opacity ' + FADE_DURATION + 'ms ease';
    slide.style.opacity = '1';
  };

  /**
   * Don't fade in an image that hasn't arrived yet.
   *
   * On a statically rendered grid the second image is usually in cache by the
   * time anyone hovers, so this is a no-op. On a grid injected after page load
   * — recommended products, quick-view, infinite scroll — it often isn't: the
   * request only starts on hover. Fading in an empty <img> just reveals the
   * featured image underneath it, and the shopper sees the wrong picture until
   * the download lands. Wait for the load, then fade.
   */
  ProductCardCarousel.prototype.startHoverWhenLoaded = function (target) {
    var slide = this.slides[target];
    if (!slide) return;

    var img = slide.querySelector('img');
    var hasSource = img && (img.currentSrc || img.getAttribute('src'));

    if (!img || (hasSource && img.complete && img.naturalWidth > 0)) {
      this.startHover();
      return;
    }

    var self = this;
    var onReady = function () {
      img.removeEventListener('load', onReady);
      img.removeEventListener('error', onReady);
      // The cursor may have moved on, or an arrow may have been pressed,
      // while the image was in flight.
      if (self.hoverActive && !self.userNavigated) self.startHover();
    };

    img.addEventListener('load', onReady);
    img.addEventListener('error', onReady);
  };

  ProductCardCarousel.prototype.startHover = function () {
    var target = this.hoverIndex();
    var slide = this.slides[target];
    if (!slide || target === this.index) return;

    this.clearTimers();
    this.flushFadingSlide();
    this.clearHoverStyles(this.hoverSlide);
    this.hoverSlide = slide;
    this.fadeSlideIn(slide, 2);

    // Once the fade is done, make it real (see settleHover).
    this.settleTimer = window.setTimeout(function () {
      this.settleTimer = null;
      this.settleHover();
    }.bind(this), FADE_DURATION + 20);
  };

  /**
   * Fold the finished fade into the carousel's actual position: move the
   * track under the fully-opaque overlay and drop the overlay in the same
   * task. Both changes are instant and cancel out visually — the pixels on
   * screen don't move — but afterwards the carousel is genuinely sitting on
   * the second image.
   *
   * This is what keeps arrow presses clean. Without it every press had to
   * jump the track first and then animate, and a jump the browser hasn't
   * painted yet can't anchor a transition, so the featured image flashed
   * back before the slide. Settling here means that by the time anyone can
   * click, the position is real and long since painted.
   */
  ProductCardCarousel.prototype.settleHover = function () {
    var slide = this.hoverSlide;
    if (!slide) return;

    var target = this.slides.indexOf(slide);
    this.hoverSlide = null;
    if (target < 0) return;

    this.goTo(target, false);
    this.clearHoverStyles(slide);
  };

  ProductCardCarousel.prototype.endHover = function () {
    var slide = this.hoverSlide;
    this.clearTimers();
    this.flushFadingSlide();
    if (!slide) return;

    this.hoverSlide = null;
    this.fadingSlide = slide;
    slide.style.transition = 'opacity ' + FADE_DURATION + 'ms ease';
    slide.style.opacity = '0';

    this.fadeOutTimer = window.setTimeout(function () {
      this.fadeOutTimer = null;
      this.fadingSlide = null;
      this.clearHoverStyles(slide);
    }.bind(this), FADE_DURATION + 50);
  };

  /**
   * Commit a fade that's still in flight when an arrow is pressed. Returns
   * true when the track was jumped, so the caller knows the following slide
   * has to wait for a paint. Returns false in the common case, where the
   * fade already settled and there's nothing to commit.
   */
  ProductCardCarousel.prototype.commitHover = function () {
    var slide = this.hoverSlide;
    this.clearTimers();
    this.flushFadingSlide();
    if (!slide) return false;

    var target = this.slides.indexOf(slide);
    this.hoverSlide = null;
    if (target < 0) return false;

    this.goTo(target, false);
    this.clearHoverStyles(slide);
    return true;
  };

  /* ---------------------------------------------------------------------
   * Returning to the featured image
   * ------------------------------------------------------------------ */

  ProductCardCarousel.prototype.startReturn = function () {
    var slide = this.slides[this.baseIndex];
    if (!slide) return;

    this.finishReturn();
    this.returnSlide = slide;
    this.fadeSlideIn(slide, 3); // sits above a hover slide still fading out

    this.returnTimer = window.setTimeout(function () {
      this.returnTimer = null;
      this.finishReturn();
    }.bind(this), FADE_DURATION + 20);
  };

  /**
   * Same instant-and-instant trick as settleHover: track under the overlay,
   * overlay away, no visible change. Returns true when the track jumped.
   */
  ProductCardCarousel.prototype.finishReturn = function () {
    if (this.returnTimer) {
      window.clearTimeout(this.returnTimer);
      this.returnTimer = null;
    }

    var slide = this.returnSlide;
    if (!slide) return false;
    this.returnSlide = null;

    this.goTo(this.baseIndex, false);
    this.clearHoverStyles(slide);
    return true;
  };

  ProductCardCarousel.prototype.onPointerEnter = function () {
    this.finishReturn();
    this.hoverActive = true;

    if (this.count < 2 || this.userNavigated) return;

    // The fade only runs from the card's resting image. While the shopper is
    // mid-browse on another image, hovering doesn't layer a fade on top of
    // it; the card returns to rest once the cursor leaves.
    if (this.index !== this.baseIndex) return;

    var target = this.hoverIndex();
    if (target === this.index) return;

    // Start the download straight away, fade once it's had a moment.
    loadSlideImage(this.slides[target]);

    this.clearTimers();
    this.hoverTimer = window.setTimeout(function () {
      this.hoverTimer = null;
      if (this.userNavigated) return;
      this.startHoverWhenLoaded(target);
    }.bind(this), HOVER_DELAY);
  };

  ProductCardCarousel.prototype.onPointerLeave = function () {
    this.hoverActive = false;
    this.endHover();
    this.userNavigated = false;

    if (!RETURN_TO_FEATURED_ON_LEAVE) return;
    if (this.count < 2 || this.index === this.baseIndex) return;

    this.startReturn();
  };

  /* ------------------------------------------------------------------ */

  ProductCardCarousel.prototype.preloadAround = function (index) {
    loadSlideImage(this.slides[index]);
    if (index + 1 < this.count) loadSlideImage(this.slides[index + 1]);
    if (index - 1 >= 0) loadSlideImage(this.slides[index - 1]);
  };

  ProductCardCarousel.prototype.goTo = function (index, animate) {
    if (this.count < 1) return;

    // Modulo rather than a single wrap, since a queued step can arrive with
    // more than one press folded into it.
    index = ((index % this.count) + this.count) % this.count;

    this.index = index;
    this.preloadAround(index);

    this.track.style.transition = animate === false ? 'none' : '';
    this.track.style.transform = 'translate3d(' + (-100 * index) + '%, 0, 0)';

    if (animate === false) this.track.offsetHeight; // eslint-disable-line no-unused-expressions

    this.slides.forEach(function (slide, slideIndex) {
      var isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      var link = slide.querySelector('.ProductItem__ImageLink');
      if (link) {
        if (isActive) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      }
    });

    if (animate === false) {
      var track = this.track;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          track.style.transition = '';
        });
      });
    }
  };

  ProductCardCarousel.prototype.goToMedia = function (mediaId) {
    for (var i = 0; i < this.slides.length; i++) {
      if (String(this.slides[i].getAttribute('data-media-id')) === String(mediaId)) {
        // A swatch selection becomes the new resting image, so hovering off
        // returns here rather than to the original featured image.
        this.clearTimers();
        this.flushFadingSlide();
        this.clearHoverStyles(this.hoverSlide);
        this.hoverSlide = null;
        this.clearHoverStyles(this.returnSlide);
        this.returnSlide = null;
        if (this.returnTimer) {
          window.clearTimeout(this.returnTimer);
          this.returnTimer = null;
        }

        this.baseIndex = i;
        this.userNavigated = false;
        this.goTo(i, true);
        return;
      }
    }
  };

  ProductCardCarousel.prototype.next = function () {
    this.queueStep(1, false);
  };

  ProductCardCarousel.prototype.prev = function () {
    this.queueStep(-1, false);
  };

  function initProductCardCarousels(scope) {
    var root = scope || document;
    var carousels = root.querySelectorAll('[data-product-card-carousel]');

    for (var i = 0; i < carousels.length; i++) {
      // The readiness check is a JS property, not the attribute. cloneNode
      // copies attributes but not properties or listeners, so a card cloned
      // out of a rendered grid — which is how plenty of recommendation and
      // slider sections build themselves — would otherwise look initialised
      // while having no working arrows at all.
      if (carousels[i]._productCardCarousel) continue;
      if (isInsideCartDrawer(carousels[i])) {
        carousels[i]._productCardCarousel = true;
        continue;
      }
      carousels[i]._productCardCarousel = new ProductCardCarousel(carousels[i]);
    }
  }

  function isInsideCartDrawer(element) {
    return !!(element.closest && element.closest('#sidebar-cart, .Drawer_recommend, .drawer-items_container'));
  }

  window.initProductCardCarousels = initProductCardCarousels;

  function boot() {
    initProductCardCarousels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', boot);
  document.addEventListener('shopify:section:load', function (event) {
    initProductCardCarousels(event.target);
  });

  var observer = new MutationObserver(function () {
    window.clearTimeout(window.__productCardCarouselTimer);
    window.__productCardCarouselTimer = window.setTimeout(function () {
      initProductCardCarousels();
    }, 50);
  });

  function observeBody() {
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.body) {
    observeBody();
  } else {
    document.addEventListener('DOMContentLoaded', observeBody);
  }
})();